import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { PauseCircle } from "@phosphor-icons/react";

// User-defined reason choices for a vessel pause. Drives downstream logic:
//   - "Breakdown" surfaces the accountability + equipment picker
//   - Everything else routes to a generic category mapping
const REASONS = [
    { value: "Hatch Change",       category: "vessel"      },
    { value: "Weather conditions", category: "external"    },
    { value: "Shift change",       category: "operations"  },
    { value: "Operational stop",   category: "operations"  },
    { value: "Breakdown",          category: "equipment"   },
];


export default function PauseModal({ vessel, onClose, onSubmitted }) {
    const [reason, setReason] = useState("");
    const [description, setDescription] = useState("");
    const [etaH, setEtaH] = useState("");
    const [etaM, setEtaM] = useState("");
    const [accountability, setAccountability] = useState("");
    const [conveyorId, setConveyorId] = useState("");
    const [transnetEquipment, setTransnetEquipment] = useState("");
    const [loading, setLoading] = useState(false);

    // Active conveyors (from current vessel routes) — used when Grindrod-accountable
    const [activeConveyors, setActiveConveyors] = useState([]);
    // Historic Transnet equipment names — used as autocomplete suggestions
    const [transnetSuggestions, setTransnetSuggestions] = useState([]);

    const isBreakdown = reason === "Breakdown";
    const showGrindrodConveyors = isBreakdown && accountability === "grindrod";
    const showTransnetEquipment = isBreakdown && accountability === "transnet";

    // Load active conveyors on the live vessel — these are the only Grindrod
    // assets that can be down right now (anything not routed isn't loading).
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const r = await api.get(`/vessels/${vessel.id}/conveyor-routes`);
                if (cancelled) return;
                const active = (r.data || []).filter((s) => s.active);
                const names = new Set();
                active.forEach((s) => (s.conveyors || []).forEach((c) => names.add(c)));
                // Resolve names → conveyor docs so we have ids for the dropdown
                if (names.size > 0) {
                    const c = await api.get("/conveyors", { params: { division: vessel.division || "RBTG" } });
                    if (cancelled) return;
                    const filtered = (c.data || []).filter((doc) => names.has(doc.name));
                    setActiveConveyors(filtered);
                } else {
                    setActiveConveyors([]);
                }
            } catch (err) {
                console.warn("Failed to load active conveyors:", err?.message || err);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [vessel.id, vessel.division]);

    // Pull recent Transnet equipment names for autocomplete (only fetched once)
    useEffect(() => {
        let cancelled = false;
        api.get("/pause-equipment-suggestions", { params: { accountability: "transnet" } })
            .then((r) => { if (!cancelled) setTransnetSuggestions(r.data?.suggestions || []); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);

    const reasonMeta = useMemo(() => REASONS.find((r) => r.value === reason), [reason]);

    const submit = async (e) => {
        e.preventDefault();
        if (!reason) {
            toast.error("Please select a reason for the pause");
            return;
        }
        if (isBreakdown && !accountability) {
            toast.error("Please choose who is accountable for the breakdown");
            return;
        }
        if (showGrindrodConveyors && !conveyorId) {
            toast.error("Please select which Grindrod conveyor is down");
            return;
        }
        if (showTransnetEquipment && !transnetEquipment.trim()) {
            toast.error("Please enter the Transnet equipment name");
            return;
        }
        if (!description.trim()) {
            toast.error("Please describe the issue");
            return;
        }
        setLoading(true);
        const totalMinutes =
            etaH === "" && etaM === ""
                ? null
                : (parseInt(etaH || "0", 10) || 0) * 60 + (parseInt(etaM || "0", 10) || 0);
        // equipment_id is the human-readable equipment label that surfaces on the dashboard.
        let equipmentLabel = null;
        if (showGrindrodConveyors) {
            equipmentLabel = activeConveyors.find((c) => c.id === conveyorId)?.name || null;
        } else if (showTransnetEquipment) {
            equipmentLabel = transnetEquipment.trim();
        }
        try {
            await api.post(`/vessels/${vessel.id}/pause`, {
                reason,
                description,
                eta_minutes: totalMinutes,
                category: reasonMeta?.category || "other",
                equipment_id: equipmentLabel,
                accountability: isBreakdown ? accountability : null,
                conveyor_id: showGrindrodConveyors ? conveyorId : null,
            });
            toast.success("Vessel loading paused");
            onSubmitted?.();
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="pause-modal">
            <div className="g-card max-w-xl w-full max-h-[90vh] overflow-y-auto">
                <div className="g-card-header-red">
                    <PauseCircle size={18} weight="fill" />
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-white/70">Pause loading</div>
                        <div className="font-display font-bold text-white">{vessel.vessel_name}</div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white text-sm" data-testid="pause-close">
                        Close
                    </button>
                </div>
                <form onSubmit={submit} className="p-6 space-y-4" data-testid="pause-form">
                    <div>
                        <label className="uppercase-label block mb-1.5">
                            Reason <span className="text-[var(--grindrod-red)]">*</span>
                        </label>
                        <select
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                // reset breakdown-specific state when reason changes
                                if (e.target.value !== "Breakdown") {
                                    setAccountability("");
                                    setConveyorId("");
                                    setTransnetEquipment("");
                                }
                            }}
                            required
                            className="g-input"
                            data-testid="pause-reason"
                        >
                            <option value="">— Select a reason —</option>
                            {REASONS.map((r) => (
                                <option key={r.value} value={r.value}>{r.value}</option>
                            ))}
                        </select>
                    </div>

                    {isBreakdown && (
                        <>
                            <div>
                                <label className="uppercase-label block mb-1.5">
                                    Accountability <span className="text-[var(--grindrod-red)]">*</span>
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => { setAccountability("grindrod"); setTransnetEquipment(""); }}
                                        className={`text-xs uppercase tracking-wide font-bold py-2.5 rounded-sm border-2 ${accountability === "grindrod" ? "bg-[var(--grindrod-navy)] text-white border-[var(--grindrod-navy)]" : "bg-white text-slate-600 border-slate-300"}`}
                                        data-testid="pause-accountability-grindrod"
                                    >
                                        Grindrod
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => { setAccountability("transnet"); setConveyorId(""); }}
                                        className={`text-xs uppercase tracking-wide font-bold py-2.5 rounded-sm border-2 ${accountability === "transnet" ? "bg-[#0F766E] text-white border-[#0F766E]" : "bg-white text-slate-600 border-slate-300"}`}
                                        data-testid="pause-accountability-transnet"
                                    >
                                        Transnet Port Terminal
                                    </button>
                                </div>
                            </div>

                            {showGrindrodConveyors && (
                                <div data-testid="pause-grindrod-conveyors">
                                    <label className="uppercase-label block mb-1.5">
                                        Conveyor down <span className="text-[var(--grindrod-red)]">*</span>
                                    </label>
                                    {activeConveyors.length === 0 ? (
                                        <div className="text-xs text-[var(--grindrod-red)] bg-[#FDE7E9] border border-[var(--grindrod-red)] rounded-sm px-3 py-2">
                                            No active conveyor route on this vessel — please activate a route first (Conveyor Route tab) so the breakdown can be allocated correctly.
                                        </div>
                                    ) : (
                                        <select
                                            value={conveyorId}
                                            onChange={(e) => setConveyorId(e.target.value)}
                                            className="g-input"
                                            required
                                            data-testid="pause-conveyor-select"
                                        >
                                            <option value="">— Select conveyor in active route —</option>
                                            {activeConveyors.map((c) => (
                                                <option key={c.id} value={c.id}>{c.name}</option>
                                            ))}
                                        </select>
                                    )}
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        Downtime will be allocated to this conveyor and impact its Reliability metric.
                                    </div>
                                </div>
                            )}

                            {showTransnetEquipment && (
                                <div data-testid="pause-transnet-equipment">
                                    <label className="uppercase-label block mb-1.5">
                                        Transnet equipment <span className="text-[var(--grindrod-red)]">*</span>
                                    </label>
                                    <input
                                        list="transnet-equipment-suggestions"
                                        value={transnetEquipment}
                                        onChange={(e) => setTransnetEquipment(e.target.value)}
                                        className="g-input"
                                        placeholder="e.g. KSF1, NCV03, Tippler 2"
                                        required
                                        data-testid="pause-transnet-equipment-input"
                                    />
                                    <datalist id="transnet-equipment-suggestions">
                                        {transnetSuggestions.map((s) => (
                                            <option key={s} value={s} />
                                        ))}
                                    </datalist>
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        Recorded as Transnet-accountable. Surfaces on the Conveyor Systems breakdown log.
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    <div>
                        <label className="uppercase-label block mb-1.5">Description</label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            className="g-input"
                            rows={3}
                            required
                            placeholder="Describe the breakdown / issue in detail."
                            data-testid="pause-description"
                        />
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1.5">
                            Estimated time to repair
                        </label>
                        <div className="grid grid-cols-2 gap-2 max-w-xs">
                            <div>
                                <input
                                    type="number"
                                    min="0"
                                    value={etaH}
                                    onChange={(e) => setEtaH(e.target.value)}
                                    className="g-input tabular"
                                    placeholder="0"
                                    data-testid="pause-eta-hours"
                                />
                                <div className="text-[10px] text-slate-500 mt-1">hours</div>
                            </div>
                            <div>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={etaM}
                                    onChange={(e) => setEtaM(e.target.value)}
                                    className="g-input tabular"
                                    placeholder="30"
                                    data-testid="pause-eta-minutes"
                                />
                                <div className="text-[10px] text-slate-500 mt-1">minutes</div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
                        <button type="button" onClick={onClose} className="btn-outline" data-testid="pause-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="pause-submit">
                            {loading ? "Pausing…" : "Pause loading"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
